import { hasResumeEvidence, sanitizeResumeFormData } from './formValidation';

const clean = (value) => String(value ?? '').trim();

const hasAny = (...values) => values.some((value) => clean(value));

const sectionLabels = {
  profile: 'Profile',
  workHistory: 'Work History',
  educationHistory: 'Education',
  skills: 'Skills',
  federalDetails: 'Federal Details',
  optionalContent: 'Optional Content',
  oneShot: 'One-Shot',
};

const pathRootLabels = {
  personalInfo: 'Profile',
};

const fieldLabels = {
  firstName: 'Name',
  lastName: 'Name',
  email: 'Email',
  phoneNumber: 'Phone Number',
  city: 'City',
  state: 'State',
  country: 'Country',
  jobTitle: 'Job Title',
  employer: 'Employer',
  startDate: 'Start Date',
  endDate: 'End Date',
  hoursPerWeek: 'Hours Per Week',
  responsibilities: 'Responsibilities',
  keyAccomplishments: 'Accomplishments',
  employerAddress: 'Employer Location',
  supervisor: 'Supervisor',
  canContactSupervisor: 'Supervisor Contact Preference',
  showOnResume: 'Show On Resume',
  institution: 'Institution',
  degree: 'Degree',
  major: 'Major',
  graduationYear: 'Graduation Year',
  certificationName: 'Certification Name',
  certificationAuthority: 'Issuing Authority',
};

export const formatReadinessPath = (path) => {
  const raw = clean(path);
  if (!raw) return '';
  const parts = raw.replace(/\[(\d+)\]/g, '.$1').split('.').filter(Boolean);
  if (!parts.length) return '';

  const root = parts[0];
  const section = pathRootLabels[root] || sectionLabels[root] || fieldLabels[root] || root;
  const fieldPart = [...parts].reverse().find((part) => !/^\d+$/.test(part) && part !== root);
  const field = fieldLabels[fieldPart] || (fieldPart ? fieldPart.replace(/([A-Z])/g, ' $1').replace(/^\w/, (char) => char.toUpperCase()) : '');
  const indexPart = parts.find((part) => /^\d+$/.test(part));
  const itemLabel = indexPart !== undefined ? `Entry ${Number(indexPart) + 1}` : '';

  return [section, itemLabel, field].filter(Boolean).join(' > ');
};

const severityWeights = {
  required: 18,
  recommended: 8,
  optional: 3,
};

const makeFinding = ({
  id,
  section,
  severity,
  message,
  path,
  actionLabel,
  canNavigate = true,
}) => ({
  id,
  section,
  sectionLabel: sectionLabels[section] || section,
  severity,
  message,
  path,
  pathLabel: formatReadinessPath(path),
  actionLabel,
  canNavigate,
});

const push = (findings, finding) => findings.push(makeFinding(finding));

const activeWork = (formData) =>
  (Array.isArray(formData.workHistory) ? formData.workHistory : []).filter(
    (job) => job?.showOnResume !== false,
  );

const hiddenWork = (formData) =>
  (Array.isArray(formData.workHistory) ? formData.workHistory : []).filter(
    (job) => job?.showOnResume === false,
  );

const activeEducation = (formData) =>
  (Array.isArray(formData.educationHistory) ? formData.educationHistory : []).filter(
    (entry) => entry?.showOnResume !== false,
  );

const hiddenEducation = (formData) =>
  (Array.isArray(formData.educationHistory) ? formData.educationHistory : []).filter(
    (entry) => entry?.showOnResume === false,
  );

const visibleEvidence = (formData) => {
  const work = activeWork(formData).some((job) =>
    hasAny(job.jobTitle, job.employer, job.responsibilities, job.keyAccomplishments),
  );
  const education = activeEducation(formData).some((entry) =>
    hasAny(
      entry.institution,
      entry.degree,
      entry.major,
      ...(Array.isArray(entry.significantProjects) ? entry.significantProjects : []),
      ...(Array.isArray(entry.relevantCourses) ? entry.relevantCourses : []),
    ),
  );
  const volunteer = (formData.volunteerWork || []).some((entry) =>
    hasAny(entry.organization, entry.role, entry.responsibilities),
  );
  const credentials = (formData.certifications || []).some((entry) =>
    hasAny(entry.certificationName, entry.certificationAuthority),
  );
  const lists = ['skills', 'selectedSkills', 'training', 'languages', 'organizations', 'awards'].some(
    (key) => Array.isArray(formData[key]) && formData[key].some((item) => clean(item)),
  );

  return work || education || volunteer || credentials || lists || clean(formData.professionalSummary);
};

const checkProfile = (formData, findings) => {
  const info = formData.personalInfo || {};
  if (!hasAny(info.firstName, info.lastName)) {
    push(findings, {
      id: 'profile-name-missing',
      section: 'profile',
      severity: 'required',
      message: 'Add your first and last name so the resume has an applicant identity.',
      path: 'personalInfo.firstName',
      actionLabel: 'Add name',
    });
  }
  if (!clean(info.email) && !clean(info.phoneNumber)) {
    push(findings, {
      id: 'profile-contact-missing',
      section: 'profile',
      severity: 'required',
      message: 'Add at least one contact method: email or phone number.',
      path: 'personalInfo.email',
      actionLabel: 'Add contact',
    });
  }
  if (!hasAny(info.city, info.state, info.country)) {
    push(findings, {
      id: 'profile-location-missing',
      section: 'profile',
      severity: 'recommended',
      message: 'Add a city, state, or country so federal reviewers can see your location.',
      path: 'personalInfo.city',
      actionLabel: 'Add location',
    });
  }
};

const checkWork = (formData, findings) => {
  const jobs = activeWork(formData);
  if (!jobs.length) {
    push(findings, {
      id: 'work-none-visible',
      section: 'workHistory',
      severity: 'required',
      message: 'Add at least one visible work, volunteer, education, or credential entry before generating a federal resume.',
      path: 'workHistory',
      actionLabel: 'Add work history',
    });
  }

  jobs.forEach((job, index) => {
    const label = clean(job.jobTitle) || clean(job.employer) || `Work entry ${index + 1}`;
    if (!clean(job.jobTitle)) {
      push(findings, {
        id: `work-${index}-title`,
        section: 'workHistory',
        severity: 'required',
        message: `${label}: add a job title.`,
        path: `workHistory.${index}.jobTitle`,
        actionLabel: 'Add title',
      });
    }
    if (!clean(job.employer)) {
      push(findings, {
        id: `work-${index}-employer`,
        section: 'workHistory',
        severity: 'required',
        message: `${label}: add the employer name.`,
        path: `workHistory.${index}.employer`,
        actionLabel: 'Add employer',
      });
    }
    if (!clean(job.startDate) || (!job.present && !clean(job.endDate))) {
      push(findings, {
        id: `work-${index}-dates`,
        section: 'workHistory',
        severity: 'required',
        message: `${label}: add start and end dates, or mark the role as present.`,
        path: `workHistory.${index}.startDate`,
        actionLabel: 'Add dates',
      });
    }
    if (!clean(job.hoursPerWeek)) {
      push(findings, {
        id: `work-${index}-hours`,
        section: 'federalDetails',
        severity: 'recommended',
        message: `${label}: add hours per week for federal formatting.`,
        path: `workHistory.${index}.hoursPerWeek`,
        actionLabel: 'Add hours',
      });
    }
    if (!clean(job.responsibilities) && !clean(job.keyAccomplishments)) {
      push(findings, {
        id: `work-${index}-duties`,
        section: 'workHistory',
        severity: 'required',
        message: `${label}: add responsibilities or accomplishments so One-Shot has evidence to use.`,
        path: `workHistory.${index}.responsibilities`,
        actionLabel: 'Add duties',
      });
    }
    if (!clean(job.employerAddress)) {
      push(findings, {
        id: `work-${index}-address`,
        section: 'federalDetails',
        severity: 'recommended',
        message: `${label}: add employer city/state for federal resume completeness.`,
        path: `workHistory.${index}.employerAddress`,
        actionLabel: 'Add employer location',
      });
    }
    if (!clean(job.supervisor)) {
      push(findings, {
        id: `work-${index}-supervisor`,
        section: 'federalDetails',
        severity: 'recommended',
        message: `${label}: add a supervisor name when available.`,
        path: `workHistory.${index}.supervisor`,
        actionLabel: 'Add supervisor',
      });
    }
    if (!clean(job.canContactSupervisor)) {
      push(findings, {
        id: `work-${index}-contact-supervisor`,
        section: 'federalDetails',
        severity: 'recommended',
        message: `${label}: choose whether the supervisor may be contacted.`,
        path: `workHistory.${index}.canContactSupervisor`,
        actionLabel: 'Set contact preference',
      });
    }
  });

  hiddenWork(formData).forEach((job, index) => {
    const label = clean(job.jobTitle) || clean(job.employer) || `Hidden work entry ${index + 1}`;
    push(findings, {
      id: `hidden-work-${index}`,
      section: 'optionalContent',
      severity: 'optional',
      message: `${label} is hidden from the resume and will not be used as visible resume evidence.`,
      path: `workHistory.${index}.showOnResume`,
      actionLabel: 'Review hidden work',
    });
  });
};

const checkEducation = (formData, findings) => {
  activeEducation(formData).forEach((entry, index) => {
    const hasEntry = hasAny(
      entry.institution,
      entry.degree,
      entry.major,
      entry.graduationYear,
      ...(Array.isArray(entry.significantProjects) ? entry.significantProjects : []),
    );
    if (!hasEntry) return;
    const label = clean(entry.institution) || `Education entry ${index + 1}`;
    if (!clean(entry.institution)) {
      push(findings, {
        id: `education-${index}-institution`,
        section: 'educationHistory',
        severity: 'required',
        message: `${label}: add the institution name.`,
        path: `educationHistory.${index}.institution`,
        actionLabel: 'Add institution',
      });
    }
    if (!clean(entry.degree) && !clean(entry.major)) {
      push(findings, {
        id: `education-${index}-credential`,
        section: 'educationHistory',
        severity: 'recommended',
        message: `${label}: add a degree, program, or major.`,
        path: `educationHistory.${index}.degree`,
        actionLabel: 'Add degree or major',
      });
    }
    if (!clean(entry.graduationYear) && !clean(entry.endDate)) {
      push(findings, {
        id: `education-${index}-year`,
        section: 'educationHistory',
        severity: 'recommended',
        message: `${label}: add a graduation year or completion date when available.`,
        path: `educationHistory.${index}.graduationYear`,
        actionLabel: 'Add year',
      });
    }
  });

  hiddenEducation(formData).forEach((entry, index) => {
    const label = clean(entry.institution) || clean(entry.degree) || `Hidden education entry ${index + 1}`;
    push(findings, {
      id: `hidden-education-${index}`,
      section: 'optionalContent',
      severity: 'optional',
      message: `${label} is hidden from the resume and will not be used as visible resume evidence.`,
      path: `educationHistory.${index}.showOnResume`,
      actionLabel: 'Review hidden education',
    });
  });
};

const checkSkills = (formData, findings) => {
  const skills = Array.isArray(formData.selectedSkills) && formData.selectedSkills.length
    ? formData.selectedSkills
    : formData.skills;
  if (!Array.isArray(skills) || !skills.some((skill) => clean(skill))) {
    push(findings, {
      id: 'skills-empty',
      section: 'skills',
      severity: 'recommended',
      message: 'Add or generate skills so the resume has a clear capabilities section.',
      path: 'skills',
      actionLabel: 'Add skills',
    });
  }
};

const checkCertifications = (formData, findings) => {
  (formData.certifications || []).forEach((entry, index) => {
    if (!hasAny(entry.certificationName, entry.certificationAuthority, entry.expiryDate)) return;
    const label = clean(entry.certificationName) || `Certification ${index + 1}`;
    if (!clean(entry.certificationName)) {
      push(findings, {
        id: `cert-${index}-name`,
        section: 'optionalContent',
        severity: 'recommended',
        message: `${label}: add the certification name.`,
        path: `certifications.${index}.certificationName`,
        actionLabel: 'Add certification name',
      });
    }
    if (!clean(entry.certificationAuthority)) {
      push(findings, {
        id: `cert-${index}-authority`,
        section: 'optionalContent',
        severity: 'recommended',
        message: `${label}: add the issuing authority.`,
        path: `certifications.${index}.certificationAuthority`,
        actionLabel: 'Add authority',
      });
    }
  });
};

const checkOneShot = (formData, findings) => {
  if (!visibleEvidence(formData) || !hasResumeEvidence(formData)) {
    push(findings, {
      id: 'one-shot-no-evidence',
      section: 'oneShot',
      severity: 'required',
      message: 'Add profile evidence before using One-Shot. It can only write from information already in the app.',
      path: 'workHistory',
      actionLabel: 'Add evidence',
    });
  }
};

export const buildResumeReadiness = (rawData = {}) => {
  const formData = sanitizeResumeFormData(rawData);
  const findings = [];

  checkProfile(formData, findings);
  checkWork(formData, findings);
  checkEducation(formData, findings);
  checkSkills(formData, findings);
  checkCertifications(formData, findings);
  checkOneShot(formData, findings);

  const counts = findings.reduce(
    (acc, finding) => {
      acc[finding.severity] = (acc[finding.severity] || 0) + 1;
      return acc;
    },
    { required: 0, recommended: 0, optional: 0 },
  );

  const deductions = findings.reduce(
    (sum, finding) => sum + (severityWeights[finding.severity] || 0),
    0,
  );
  const score = Math.max(0, 100 - deductions);
  const status = counts.required > 0
    ? 'Not Ready'
    : counts.recommended > 0
      ? 'Needs Attention'
      : 'Ready';

  const sectionSummaries = Object.entries(sectionLabels).map(([section, label]) => ({
    section,
    label,
    count: findings.filter((finding) => finding.section === section).length,
  }));

  return {
    status,
    score,
    counts,
    findings,
    sectionSummaries,
  };
};

export const readinessSectionLabels = sectionLabels;
